import { Button } from '@mantine/core';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { LOGIN_METHOD, type SecurityLogEntry, type SecurityLogQuery } from '@/api';
import { DataTable } from '@/components/Ui/DataTable';
import { EmptyText } from '@/components/Ui/EmptyText';
import { Section, SectionBody } from '@/components/Ui/Section';
import { Tag } from '@/components/Ui/Tag';
import { useIdpHandoff } from '@/hooks/useIdpHandoff';
import { apiOrigin } from '@/lib/origins';
import { useRustyState } from '@/state';
import shared from '../AccountScreens.module.css';
import { formatWhen } from '../format';
import { deviceText, eventText, methodText, placeText } from './history-text';

const PER_PAGE = 50;

type Show = 'all' | 'failed' | 'signins';

const FILTERS: Record<Show, SecurityLogQuery> = {
  all: {},
  failed: { success: false },
  signins: { category: 'sign_in' },
};

export function SignInHistory() {
  const { t, i18n } = useTranslation();
  const session = useRustyState((s) => s.api?.auth?.getSession());
  const isOidc = session?.method === LOGIN_METHOD.OIDC;
  const { accountManagementUri } = useIdpHandoff(apiOrigin());

  const [rows, setRows] = useState<SecurityLogEntry[] | null>(null);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [busy, setBusy] = useState(false);
  const [failed, setFailed] = useState(false);
  const [show, setShow] = useState<Show>('all');

  // A filter change while a page is loading must not let the older answer win.
  const latest = useRef(0);

  const load = useCallback(async (next: number, filter: Show) => {
    const request = ++latest.current;
    setBusy(true);
    try {
      const res = await useRustyState
        .getState()
        .api.getSecurityLog({ page: next, per_page: PER_PAGE, ...FILTERS[filter] });
      if (request !== latest.current) return;
      setRows((prev) => (next === 0 || !prev ? res.data : [...prev, ...res.data]));
      setTotal(res.total);
      setPage(next);
      setFailed(false);
    } catch {
      if (request !== latest.current) return;
      setFailed(true);
      setRows((prev) => prev ?? []);
    } finally {
      setBusy(false);
    }
  }, []);

  useEffect(() => {
    void load(0, show);
  }, [load, show]);

  const when = { today: t('deliveries.today'), yesterday: t('deliveries.yesterday') };
  const empty = failed
    ? t('security.history.loadFailed')
    : show === 'failed'
      ? t('security.history.emptyFailed')
      : show === 'signins'
        ? t('security.history.emptySignins')
        : t('security.history.empty');

  return (
    <Section
      id="history"
      title={t('security.history.title')}
      actions={
        <label className={`${shared.help} ${shared.row}`} style={{ gap: 8 }}>
          {t('security.history.show')}
          <select
            className={`${shared.nativeInput} ${shared.nativeInputSmall}`}
            value={show}
            onChange={(e) => {
              setRows(null);
              setShow(e.currentTarget.value as Show);
            }}
          >
            <option value="all">{t('security.history.all')}</option>
            <option value="failed">{t('security.history.failed')}</option>
            <option value="signins">{t('security.history.signins')}</option>
          </select>
        </label>
      }
    >
      {isOidc && (
        <SectionBody>
          <span className={shared.help}>
            {t('security.history.oidcNote')}{' '}
            {accountManagementUri && (
              <a href={accountManagementUri}>{t('security.history.oidcLink')}</a>
            )}
          </span>
        </SectionBody>
      )}
      {rows === null ? (
        <SectionBody>
          <EmptyText>{t('ui.loading')}</EmptyText>
        </SectionBody>
      ) : (
        <DataTable
          caption={t('security.history.title')}
          rows={rows}
          rowKey={(r) => r.id}
          empty={empty}
          columns={[
            {
              key: 'when',
              header: t('security.history.colWhen'),
              render: (r) => (
                <span className={shared.nowrap} title={new Date(r.timestamp).toLocaleString()}>
                  {formatWhen(r.timestamp, when, i18n.language)}
                </span>
              ),
            },
            {
              key: 'what',
              header: t('security.history.colWhat'),
              render: (r) => eventText(t, r.operation_label),
            },
            {
              key: 'method',
              header: t('security.history.colMethod'),
              render: (r) => methodText(t, r.method),
            },
            {
              key: 'where',
              header: t('security.history.colWhere'),
              render: (r) => placeText(r.location),
            },
            {
              key: 'device',
              header: t('security.history.colDevice'),
              render: (r) => deviceText(t, r.device),
            },
            {
              key: 'result',
              header: t('security.history.colResult'),
              render: (r) =>
                r.success ? (
                  <Tag variant="ok">{t('security.history.succeeded')}</Tag>
                ) : (
                  <Tag variant="risk">{t('security.history.failedTag')}</Tag>
                ),
            },
          ]}
        />
      )}
      {rows !== null && rows.length < total && (
        <div className={shared.footer}>
          <span className={shared.help}>
            {t('security.history.loaded', { loaded: rows.length, total })}
          </span>
          <Button size="xs" variant="default" loading={busy} onClick={() => load(page + 1, show)}>
            {t('security.history.older')}
          </Button>
        </div>
      )}
    </Section>
  );
}
